// Réglages de lecture du son d'ambiance d'une scène (champ `scene.ambiance_lecture`).
// Stockés à part de `audio_assets.ambiance` pour survivre au remplacement ou à la
// suppression du fichier. Module partagé à l'identique entre hub et diffusion.
//
// - lecture_auto : l'ambiance démarre seule à l'entrée dans la scène.
// - boucle : l'ambiance reprend au début une fois terminée.
// - controles : barre de lecture (lecture, pause, arrêt, timeline) centrée en bas
//   de la scène. Tous les réglages suivants ne concernent que cette barre.
// - couleur_fond / opacite_fond_pct : fond de la barre (l'opacité ne touche que le
//   fond, les contrôles restent pleinement lisibles).
// - couleur_controles : boutons, timeline et temps.
// - arrondi_px, marge_interne_px, marge_basse_px, marge_laterale_px : en pixels CSS
//   du lecteur web ; la barre occupe toute la largeur de la scène moins deux fois
//   la marge latérale.

export const AMBIANCE_LECTURE_DEFAUTS = Object.freeze ({
  lecture_auto : true,
  boucle : true,
  controles : false,
  couleur_fond : "#000000",
  couleur_controles : "#ffffff",
  opacite_fond_pct : 60,
  arrondi_px : 12,
  marge_interne_px : 8,
  marge_basse_px : 16,
  marge_laterale_px : 16,
});

export const AMBIANCE_LECTURE_LIMITES = Object.freeze ({
  opacite_fond_pct : {min : 0, max : 100},
  arrondi_px : {min : 0, max : 200},
  marge_interne_px : {min : 0, max : 100},
  marge_basse_px : {min : 0, max : 400},
  marge_laterale_px : {min : 0, max : 1000},
});

export const AMBIANCE_LECTURE_CLES_BOOLEENNES = Object.freeze (["lecture_auto", "boucle", "controles"]);
export const AMBIANCE_LECTURE_CLES_COULEURS = Object.freeze (["couleur_fond", "couleur_controles"]);
export const AMBIANCE_LECTURE_CLES_NOMBRES = Object.freeze (Object.keys (AMBIANCE_LECTURE_LIMITES));

function borne_entier (pValeur, pMin, pMax, pDefaut)
  {
    const tBrut = Number (pValeur);
    const tNombre = Number.isFinite (tBrut) ? Math.round (tBrut) : pDefaut;
    return Math.max (pMin, Math.min (pMax, tNombre));
  }

function normalise_couleur_hex (pValeur, pDefaut)
  {
    const tSource = typeof pValeur === "string" ? pValeur.trim ().toLowerCase () : "";
    if (/^#[0-9a-f]{6}$/.test (tSource)) return tSource;
    if (/^#[0-9a-f]{3}$/.test (tSource))
      return "#" + tSource.slice (1).split ("").map ((pCar) => pCar + pCar).join ("");
    return pDefaut;
  }

export function normalise_ambiance_lecture_scene (pValeur)
  {
    const tSource = pValeur && typeof pValeur === "object" && !Array.isArray (pValeur)
      ? pValeur
      : {};
    const tResultat = {};
    for (const tCle of AMBIANCE_LECTURE_CLES_BOOLEENNES)
      tResultat[tCle] = typeof tSource[tCle] === "boolean" ? tSource[tCle] : AMBIANCE_LECTURE_DEFAUTS[tCle];
    for (const tCle of AMBIANCE_LECTURE_CLES_COULEURS)
      tResultat[tCle] = normalise_couleur_hex (tSource[tCle], AMBIANCE_LECTURE_DEFAUTS[tCle]);
    for (const tCle of AMBIANCE_LECTURE_CLES_NOMBRES)
      {
        const tLimites = AMBIANCE_LECTURE_LIMITES[tCle];
        tResultat[tCle] = borne_entier (tSource[tCle], tLimites.min, tLimites.max, AMBIANCE_LECTURE_DEFAUTS[tCle]);
      }
    return tResultat;
  }

// Fond de la barre en rgba, opacité incluse.
export function couleur_fond_ambiance_rgba (pReglages)
  {
    const tReglages = normalise_ambiance_lecture_scene (pReglages);
    const tHex = tReglages.couleur_fond;
    const tR = parseInt (tHex.slice (1, 3), 16);
    const tV = parseInt (tHex.slice (3, 5), 16);
    const tB = parseInt (tHex.slice (5, 7), 16);
    return `rgba(${tR}, ${tV}, ${tB}, ${tReglages.opacite_fond_pct / 100})`;
  }
